<template>
    <div class="summary-card" :class="{ 'summary-card-highlight': highlight }"
        :style="{ '--summary-card-accent': accentColor }">

        <div class="summary-card-header">
            <span class="summary-card-icon">
                <i class="pi" :class="icon"></i>
            </span>
            <span class="summary-card-title">{{ title }}</span>
        </div>

        <div class="summary-card-amount">{{ formatAmountToCurrency(amount) }}</div>

        <div class="summary-card-metrics">
            <div class="summary-card-metric">
                <span class="summary-card-metric-label">Transações</span>
                <span class="summary-card-metric-value">{{ count || 0 }}</span>
            </div>
            <div class="summary-card-metric">
                <span class="summary-card-metric-label">Taxas</span>
                <span class="summary-card-metric-value summary-card-metric-tax">{{ formatAmountToCurrency(totalTax) }}</span>
            </div>
            <div class="summary-card-metric">
                <span class="summary-card-metric-label">Líquido</span>
                <span class="summary-card-metric-value summary-card-metric-accent">{{ formatAmountToCurrency(calculatedAmount) }}</span>
            </div>
        </div>

        <div class="summary-card-bar">
            <span :style="{ width: barWidth }"></span>
        </div>

    </div>
</template>

<script>

import { computed } from 'vue'

export default {
    name: 'SummaryCard',
    props: {
        title: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: 'pi-chart-line',
        },
        accentColor: {
            type: String,
            default: 'var(--primary-color)',
        },
        amount: {
            type: Number,
            default: 0,
        },
        count: {
            type: Number,
            default: 0,
        },
        totalTax: {
            type: Number,
            default: 0,
        },
        calculatedAmount: {
            type: Number,
            default: 0,
        },
        // proporção do valor deste card em relação ao maior card, de 0 a 1
        share: {
            type: Number,
            default: 0,
        },
        highlight: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {

        const barWidth = computed(() => `${Math.min(Math.max(props.share || 0, 0), 1) * 100}%`)

        function formatAmountToCurrency(amount) {
            if (!amount) amount = 0
            return parseFloat(amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }

        return {
            barWidth,
            formatAmountToCurrency,
        }

    },
}
</script>

<style lang="scss" scoped>
.summary-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.25rem;
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.summary-card-header {
    display: flex;
    align-items: center;
    gap: 0.45rem;
}

.summary-card-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--summary-card-accent);

    i {
        font-size: 0.95rem;
    }
}

.summary-card-title {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.summary-card-amount {
    font-size: clamp(1.4rem, 2vw, 1.85rem);
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    color: var(--surface-900);
}

.summary-card-highlight .summary-card-amount {
    color: var(--summary-card-accent);
}

/* uma métrica por linha (rótulo à esquerda, valor à direita): em três colunas os valores
 * em reais não cabiam sem cortar, e é o mesmo formato do .limit-list das configurações */
.summary-card-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.85rem;
    border-top: 1px solid var(--surface-border);
}

.summary-card-metric {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    min-width: 0;
}

.summary-card-metric-label {
    font-size: 0.85rem;
    color: var(--text-color-secondary);
    white-space: nowrap;
}

.summary-card-metric-value {
    font-size: 1rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--surface-900);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.summary-card-metric-tax {
    color: var(--pill-warn-fg);
}

.summary-card-metric-accent {
    color: var(--summary-card-accent);
}

.summary-card-bar {
    height: 4px;
    border-radius: 999px;
    background: var(--surface-100);
    overflow: hidden;
}

.summary-card-bar span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--summary-card-accent);
    transition: width 0.4s ease;
}
</style>
